import { useState, type FormEvent } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { auth, type MeResponse } from '../../api/auth'
import { ApiError } from '../../api/client'

/**
 * Login clásico email/password + Google. Es el camino de MANAGERS y staff
 * de club, y el único que sirve para `/admin`: el código de 6 dígitos del
 * jugador identifica sólo dentro del club de un kit, así que no puede
 * abrir superficies de gestión.
 *
 * Mismo orden y copy que el LoginForm del vr-app (`vr-app/src/App.tsx`)
 * para que la marca se lea igual en las dos superficies:
 *
 *   [ Ingresar ] [ Crear cuenta ]   ← tabs
 *   [ Continuar con Google     ]   ← OAuth
 *   ────── o con email ──────       ← divider
 *   [ email o usuario          ]
 *   [ contraseña (6+)          ]
 *   [ nombre (opcional)        ]    ← signup only
 *   [ Entrar / Crear cuenta    ]
 *   error                            ← if any
 *   [ Soy jugador              ]    ← vuelve al pad de código
 */
export function ManagerLoginForm({
  next,
  presetEmail,
  invite,
  initialError,
  onAuthenticated,
  onPlayer,
}: {
  /** `?next=` crudo — sólo para el ida y vuelta del OAuth de Google. */
  next: string | null
  presetEmail: string
  /** Contexto de la invitación que trajo al usuario acá (`/invite/:token`
   *  lo cuelga del query). Sólo para el banner: qué cuestionario le piden y
   *  de parte de qué club. `null` cuando no viene de una invitación. */
  invite?: { code: string; name: string; club: string } | null
  initialError: string | null
  onAuthenticated: (user: MeResponse) => void
  onPlayer: () => void
}) {
  const { login, signup } = useAuth()

  // Si llegamos desde /invite/:token o desde un link con `?email=`, el
  // backend conoce el mail del destinatario — lo precargamos y por default
  // arrancamos en modo signup (más probable que sea cuenta nueva).
  const [mode, setMode] = useState<'signin' | 'signup'>(presetEmail ? 'signup' : 'signin')
  const [identifier, setIdentifier] = useState(presetEmail)
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(initialError)

  const looksLikeEmail = identifier.includes('@')
  const signupReady = looksLikeEmail && identifier.trim().length > 3
  const signinReady = identifier.trim().length > 0
  const canSubmit =
    (mode === 'signin' ? signinReady : signupReady) && password.length >= 6 && !busy

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      const me =
        mode === 'signin'
          ? await login(identifier.trim().toLowerCase(), password)
          : await signup(
              identifier.trim().toLowerCase(),
              password,
              displayName.trim() || undefined,
            )
      onAuthenticated(me)
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Error de red.'
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="panel flex max-w-sm flex-col gap-[10px] p-xl"
      autoComplete="on"
    >
      {presetEmail && (
        <div className="banner-ok text-center">
          {inviteHeadline(invite)}
          <br />
          <span className="text-text-muted">
            Si ya tenés cuenta, usá <strong>Ingresar</strong>; si no, creá una con tu password.
          </span>
        </div>
      )}

      <div className="flex gap-[6px]">
        {(['signin', 'signup'] as const).map((m) => {
          const selected = m === mode
          return (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={[
                'flex-1 rounded px-md py-sm text-sm font-medium transition-colors',
                selected
                  ? 'border border-primary bg-primary text-primary-fg'
                  : 'border border-outline bg-transparent text-text-secondary hover:bg-surface-highlight',
              ].join(' ')}
            >
              {m === 'signin' ? 'Ingresar' : 'Crear cuenta'}
            </button>
          )
        })}
      </div>

      {/* El OAuth vuelve por el backend, así que el ruteo por rol del SPA no
          corre: le pasamos el `next` crudo (o `/`, donde la HomePage manda al
          staff sin perfil de jugador a `/club`). */}
      <a
        href={auth.googleLoginUrl(next ?? '/')}
        className={[
          'flex items-center justify-center gap-md rounded border border-outline bg-surface-elevated px-md py-sm',
          'text-base font-medium text-text-primary no-underline transition-colors',
          'hover:border-primary hover:bg-surface-highlight',
          busy ? 'pointer-events-none opacity-60' : '',
        ].join(' ')}
      >
        <GoogleIcon />
        <span>Continuar con Google</span>
      </a>

      <div className="my-xs flex items-center gap-md text-xs text-text-muted">
        <div className="h-px flex-1 bg-outline" />
        <span>o con email</span>
        <div className="h-px flex-1 bg-outline" />
      </div>

      <input
        type={mode === 'signin' ? 'text' : 'email'}
        value={identifier}
        onChange={(e) => setIdentifier(e.target.value)}
        placeholder={mode === 'signin' ? 'email o usuario' : 'email@dominio.com'}
        autoFocus={!presetEmail}
        autoComplete={mode === 'signin' ? 'username' : 'email'}
        inputMode={mode === 'signin' ? 'text' : 'email'}
        className="input"
      />
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="contraseña (6+)"
        autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
        autoFocus={!!presetEmail}
        className="input"
      />
      {mode === 'signup' && (
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="nombre para mostrar (opcional)"
          maxLength={64}
          className="input"
        />
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className={[
          'rounded px-md py-sm text-base font-medium transition-colors',
          canSubmit
            ? 'bg-primary text-primary-fg hover:brightness-110'
            : 'bg-surface-elevated text-text-primary opacity-60',
        ].join(' ')}
      >
        {busy ? '…' : mode === 'signin' ? 'Entrar' : 'Crear cuenta'}
      </button>

      {error && <div className="text-center text-xs text-text-muted">{error}</div>}

      <button
        type="button"
        onClick={onPlayer}
        className="btn-ghost mt-xs px-md py-sm text-sm text-text-secondary"
      >
        Soy jugador
      </button>
    </form>
  )
}

/**
 * Copy del banner de invitación. Con el contexto que manda `/invite/:token`
 * nombra el cuestionario y el club ("Te invitaron a completar Dureza Mental
 * (SMTQ) de parte de Club X"); sin él cae al genérico de siempre (links
 * viejos con sólo `?email=`, donde el frontend no sabe qué le piden).
 */
function inviteHeadline(
  invite?: { code: string; name: string; club: string } | null,
): string {
  const what = invite?.name || invite?.code
  if (!what) return 'Te invitaron a completar algo en VerSports.'
  const from = invite?.club ? ` de parte de ${invite.club}` : ''
  return `Te invitaron a completar ${what}${from} en VerSports.`
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}
